import classNames from "classnames"
import { ComponentProps, useCallback, useEffect, useMemo, useRef } from "react"
import { useController } from "react-hook-form"
import ReactSelect from "react-select"
import { useTranslation } from "react-i18next"
import { FieldOption } from "../../types/FieldOption"
import { FieldProps } from "../../types/FieldProps"
import { FormErrorText } from "../FormErrorText"
import { FormHelperText } from "../FormHelperText"
import { FormLabel } from "../FormLabel"

export interface MultiSelectProps extends FieldProps {
  selectProps?: ComponentProps<typeof ReactSelect>
  options: FieldOption[]
  isMulti?: boolean
  className?: string
}

export function Select(props: MultiSelectProps) {
  // Hacky-ish solution to add accessibility to react-select
  const addButtonRole = () => {
    Array.from(
      document.getElementsByClassName("react-select__clear-indicator"),
    ).forEach((item) => item.setAttribute("role", "button"))
  }

  const addTabIndex = () => {
    Array.from(
      document.getElementsByClassName("react-select__multi-value__remove"),
    ).forEach((item) => item.setAttribute("tabindex", "0"))
  }

  const {
    field,
    fieldState: { error },
  } = useController(props)

  useEffect(() => {
    addButtonRole()
    addTabIndex()
  }, [field.value])

  const selectRef = useRef<any>()
  const { t } = useTranslation()
  const {
    className,
    options,
    name,
    selectProps,
    label,
    helperText,
    isMulti,
    placeholder,
    optional,
  } = props

  const handleChange = useCallback(
    (opts: FieldOption | FieldOption[]) => {
      if (Array.isArray(opts)) {
        const value = opts.map(({ value }) => value)
        field.onChange(value)
      } else {
        field.onChange(opts.value)
      }
    },
    [field],
  )

  const fieldValue = useMemo(() => {
    if (Array.isArray(field.value)) {
      return options.filter((option) => field.value?.includes(option.value))
    } else {
      return options.find((option) => option.value === field.value)
    }
  }, [field.value, options])

  const placeholderText = `${placeholder || t("admin.inputs.select")}...`

  return (
    <div className={classNames("flex flex-col", className)}>
      <FormLabel className="pb-[var(--alias-padding-xxs)]" htmlFor={name}>
        {label || name}
        {!optional && <span className="text-red-500">*</span>}
      </FormLabel>
      <ReactSelect
        placeholder={placeholderText}
        isMulti={isMulti}
        onKeyDown={(e) => {
          if (!selectRef.current.props.menuIsOpen) {
            return e.key === "Enter" ? selectRef.current?.openMenu() : null
          }
        }}
        // these are necessary in order to override react-select styles via classNames
        className="react-select"
        classNamePrefix="react-select"
        // applying error with id and checking for partial match in ../../index.css
        // this is related to this issue https://github.com/romgain/react-select-event/issues/20
        // originally error state was assigned via a className in a custom Control component, however it broke the tests
        id={error ? `withError_${name}` : `withoutError_${name}`}
        options={options}
        // this is the actual id that gets matched with our label component
        inputId={name}
        {...field}
        onChange={handleChange}
        value={fieldValue as any}
        {...selectProps}
        ref={selectRef}
      />
      {helperText?.length ? (
        <FormHelperText>{helperText}</FormHelperText>
      ) : null}
      {error && (
        <FormErrorText className="pt-[var(--alias-padding-xxs)]">
          {error.message}
        </FormErrorText>
      )}
    </div>
  )
}
